import cn from 'classnames';
import * as React from 'react';
import { memo, useCallback, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import MediaQuery from 'react-responsive';
import { useTranslation } from 'react-i18next';

import { addPassenger, removePassenger, setPassengers } from '../../../store/passengers/actions';
import { getPassengersErrors, getPassengersState, getTotalPassengersCount } from '../../../store/passengers/selectors';
import { SearchFormState } from '../../../store/reducers';
import { getIsSegmentsInternational, getSegments, isMultiCity } from '../../../store/segments/selectors';
import { Passenger as PassengerSchema } from '../../../../Passengers/types';
import Dropdown from '../../../../Dropdown';
import { MOBILE_MIN_WIDTH, useIsMobile } from '../../../../utils';
import { Passengers as PassengersComponent } from '../../../../Passengers/components/Passengers';
import { useConfig } from '../../../../context';
import { useTheme } from '../../../../theme';
import Tooltip from '../../../../BaseComponents/Tooltip/Tooltip';
import { PassengersState } from '../../../store/passengers/reducers';
import { canBeDecrease, canBeIncrease, isRoutesMatch } from './utils';
import { Segment } from '../../../schemas/Segment';
import { useIsWithSelectableCurrencies } from '../hooks';
import { getRedeemMiles } from '../../../store/selectors';
import ModalConfirm from '../../../../ModalConfirm';
import { PassengerTypeConfig } from 'SearchForm/types';
import { getSelectedFareGroup } from '../../../store/fareGroup/selectors';
import { setSelectedFareGroup } from '../../../store/fareGroup/actions';
import { CdnIcon } from '@shared/ui';

interface StateProps {
	isMultiCity: boolean;
	passengers: PassengersState;
	passengersCount: number;
	segments: Segment[];
	redeemMiles?: boolean;
	selectedFareGroup?: string;
}

interface DispatchProps {
	addPassenger: typeof addPassenger;
	removePassenger: typeof removePassenger;
	setPassengers: typeof setPassengers;
	setSelectedFareGroup: typeof setSelectedFareGroup;
}

interface OwnProps {
	customCanBeIncrease?: typeof canBeIncrease;
	customCanBeDecrease?: typeof canBeDecrease;
}

type Props = StateProps & DispatchProps & OwnProps;

const passengerIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<g opacity="0.5">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M12 12C13.66 12 15 10.66 15 9C15 7.34 13.66 6 12 6C10.34 6 9 7.34 9 9C9 10.66 10.34 12 12 12ZM12 13.9C10 13.9 6 15 6 17V18H18V17C18 15 14 13.9 12 13.9Z"
				fill="white"
			/>
		</g>
	</svg>
);

const Passengers = memo<Props>(
	({
		isMultiCity,
		passengers,
		selectedFareGroup,
		setSelectedFareGroup,
		setPassengers,
		addPassenger,
		removePassenger,
		passengersCount,
		segments,
		redeemMiles,
		customCanBeIncrease,
		customCanBeDecrease
	}) => {
		const { Passengers: css } = useTheme('SearchForm');

		const validation = useSelector(getPassengersErrors);

		const {
			global: {
				companyInfo: { iataCode }
			},
			SearchForm: { mobileFormType, maxPassengersCount, passengersConfig }
		} = useConfig();

		const isSegmentsInternational = useSelector((state: SearchFormState) =>
			getIsSegmentsInternational(state, iataCode)
		);

		const alternative = mobileFormType !== 'common';
		const withSelectableCurrencies = useIsWithSelectableCurrencies();
		const { t } = useTranslation(['SearchForm', 'PassengerTypes']);
		const isMobile = useIsMobile();

		const [isOpen, setIsOpen] = useState(false);
		const buttonRef = useRef<HTMLButtonElement>();
		const [isTooltipOpen, setIsTooltipOpen] = useState(false);

		const open = useCallback(() => setIsOpen(!isOpen), [isOpen]);
		const close = useCallback(() => setIsOpen(false), []);
		const focus = useCallback((event: React.FocusEvent<HTMLButtonElement>) => event.target.blur(), []);
		const onClose = useCallback(
			(event: React.MouseEvent) => {
				if (event.target !== buttonRef.current) {
					close();
				}
			},
			[buttonRef]
		);

		const filterPassengers = () => {
			const state: PassengersState = { ...passengers };
			passengersConfig
				.filter(it => it.blockPayByMiles)
				.map(it => {
					state[it.code] = 0;
				});
			setPassengers(state);
		};

		React.useEffect(() => {
			if (redeemMiles) {
				filterPassengers();
			}
		}, [redeemMiles]);

		React.useLayoutEffect(() => {
			if (validation) {
				setTimeout(() => {
					// чтобы не смещался тултип при открытии дропдауна
					setIsTooltipOpen(true);
				}, 0);
			} else {
				setIsTooltipOpen(false);
			}
		}, [validation]);

		const fareGroupsMap = new Map<string, PassengerTypeConfig[]>();
		if (passengersConfig?.length) {
			passengersConfig.forEach(passenger => {
				const group = passenger.group ?? 'Standard';
				fareGroupsMap.set(group, [...(fareGroupsMap.get(group) ?? []), passenger]);
			});
		}

		// группа может остаться в кеше юзера но ее например убрали в обнове - тогда сброс
		if (!fareGroupsMap.get(selectedFareGroup)) {
			setSelectedFareGroup('Standard');
		}

		const passengerSchemas: PassengerSchema[] = React.useMemo(() => {
			return (
				fareGroupsMap
					.get(selectedFareGroup)
					?.filter(passenger => {
						if (passenger.disabled || (redeemMiles && passenger?.blockPayByMiles)) {
							return false;
						}

						if (!passenger.onlyRoutes && !passenger.disabledRoutes) {
							return true;
						}

						let isRouteAvailable = true;

						if (
							passenger?.disabledRoutes?.some(routes => {
								if (routes.length === segments.length) {
									return routes.every((route, index) => isRoutesMatch(route, segments[index]));
								}

								return false;
							})
						) {
							isRouteAvailable = false;
						} else if (passenger?.onlyRoutes) {
							isRouteAvailable = passenger.onlyRoutes.some(routes => {
								if (routes.length === segments.length) {
									return routes.every((route, index) => isRoutesMatch(route, segments[index]));
								}

								return false;
							});
						}

						if (isRouteAvailable) {
							return true;
						} else {
							if (passengers[passenger.code] > 0) {
								setPassengers({
									...passengers,
									[passenger.code]: 0
								});
							}

							return false;
						}
					})
					.map(passenger => {
						return {
							type: passenger.code as any,
							count: passengers[passenger.code] || 0,
							modifyCount: passengersConfig.find(conf => conf.code === passenger.code)?.modifyCount,
							canBeIncreased: customCanBeIncrease
								? customCanBeIncrease(
										passenger,
										passengersConfig,
										passengers,
										passengersCount,
										maxPassengersCount
								  )
								: canBeIncrease(
										passenger,
										passengersConfig,
										passengers,
										passengersCount,
										maxPassengersCount
								  ),
							canBeDecreased: customCanBeDecrease
								? customCanBeDecrease(passenger, passengersConfig, passengers)
								: canBeDecrease(passenger, passengersConfig, passengers)
						};
					}) || []
			);
		}, [passengersConfig, passengers, segments, redeemMiles, selectedFareGroup]);

		const setCurrentGroup = (val: string) => {
			setSelectedFareGroup(val);
		};

		return (
			<div
				className={cn(css.passengers, {
					[css.passengers_isMultiCity]: isMultiCity,
					[css.passengers_withCurrency]: withSelectableCurrencies,
					[css.alternative]: alternative
				})}
			>
				<Tooltip
					tooltipClassName={css.tooltip}
					popperClassName={css.tooltip__popper}
					title={t(validation?.error, {
						passengerTypes: validation?.interpolateData
							?.map(required => t(`PassengerTypes:${required}`))
							?.join(', ')
					})}
					open={isTooltipOpen && (isMobile ? !isOpen : true)}
					PopperProps={{
						modifiers: [{ name: 'offset', options: { offset: [0, -5] } }]
					}}
					disableInteractive
				>
					<button
						type="button"
						data-test-id="passengers-dropdown"
						ref={buttonRef}
						className={cn(css.selector, { [css.selector_focused]: isOpen })}
						onClick={open}
						onFocus={focus}
					>
						<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
							<span className={css.passengerIcon}>{passengerIcon}</span>
						</MediaQuery>

						<span className={css.title}>
							{passengersCount} {t('passenger', { count: passengersCount })}
						</span>

						<span className={cn(css.arrowIcon, { [css.arrowIcon_flipped]: isOpen })}>
							<CdnIcon icon={'Arrow/Drop_down'} />
						</span>
					</button>
				</Tooltip>

				<MediaQuery minWidth={MOBILE_MIN_WIDTH + 1}>
					<Dropdown isOpen={isOpen} onClose={onClose}>
						<div className={css.menu}>
							<PassengersComponent
								passengers={passengerSchemas}
								fareGroups={[...fareGroupsMap.keys()]}
								currentGroup={selectedFareGroup}
								setCurrentGroup={setCurrentGroup}
								removePassenger={removePassenger}
								addPassenger={addPassenger}
								isSegmentsInternational={isSegmentsInternational}
							/>
						</div>
					</Dropdown>
				</MediaQuery>

				<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
					<ModalConfirm
						isOpen={isOpen}
						onClose={close}
						title={t('Passengers')}
						classes={{
							title: css.modal__Title,
							closeButton: css.modal__closeButton,
							swipeBlock: css.modal__swipeBlock
						}}
					>
						<div className={css.mobileMenu}>
							<div className={css.mobileContent}>
								<PassengersComponent
									passengers={passengerSchemas}
									removePassenger={removePassenger}
									addPassenger={addPassenger}
									fareGroups={[...fareGroupsMap.keys()]}
									currentGroup={selectedFareGroup}
									setCurrentGroup={setCurrentGroup}
								/>
							</div>
						</div>
					</ModalConfirm>
				</MediaQuery>
			</div>
		);
	}
);

const mapStateToProps = (state: SearchFormState): StateProps => {
	return {
		isMultiCity: isMultiCity(state),
		passengers: getPassengersState(state),
		passengersCount: getTotalPassengersCount(state),
		segments: getSegments(state),
		redeemMiles: getRedeemMiles(state),
		selectedFareGroup: getSelectedFareGroup(state)
	};
};

const mapDispatchToProps = {
	addPassenger,
	removePassenger,
	setPassengers,
	setSelectedFareGroup
};

export default connect<StateProps, DispatchProps, any, SearchFormState>(
	mapStateToProps,
	mapDispatchToProps
)(Passengers);
